import { type QConfigTableData } from '@exam-management/common/types';
import { useQConfig } from '@exam-management/feature-qconfig-provider';
import { QConfigStructureCategory } from '@exam-management/graphql';
import { useMemo } from 'react';

import { removeStructuresAndItsChildrenByQuestionType } from '../../utils';
import { useQuestionConditionsData } from '../use-question-conditions-data';

interface UseGetItemSetQuestionConditionsDataInput {
  chapterId: string;
  questionSize?: number;
  questionTypeRow?: QConfigTableData;
}

export function useGetItemSetQuestionConditionsData(
  input: Readonly<UseGetItemSetQuestionConditionsDataInput>,
) {
  const { chapterId, questionSize, questionTypeRow } = input;
  const {
    qConfigData: { id: qConfigId, structures: rawStructures },
  } = useQConfig();

  const structures = useMemo(() => {
    const structureWithoutSingleQuestions = removeStructuresAndItsChildrenByQuestionType({
      structures: rawStructures,
      removeTargetCategory: QConfigStructureCategory.Single,
    });

    if (!questionSize) {
      return structureWithoutSingleQuestions;
    }

    return removeStructuresAndItsChildrenByQuestionType({
      structures: structureWithoutSingleQuestions,
      removeTargetCategory: QConfigStructureCategory.ItemSet,
      questionSize,
    });
  }, [rawStructures, questionSize]);

  const chapterData = structures.find((structure) => structure.id === chapterId);

  if (!chapterData) {
    throw new Error('Chapter data not found');
  }

  const { levelItemsWithLevel, isLoading, checkExistOnStructures } = useQuestionConditionsData({
    qConfigId,
    chapterData,
    structures,
    questionTypeRow,
  });

  const defaultLOSRefIds = useMemo(() => {
    return structures
      .filter(
        (row) =>
          row.category === QConfigStructureCategory.LearningObjectiveStatement &&
          row.parentId === questionTypeRow?.id,
      )
      .map((row) => row.refId)
      .filter((refId): refId is string => !!refId);
  }, [structures, questionTypeRow]);

  return {
    structures,
    chapterData,
    levelItemsWithLevel,
    defaultLOSRefIds,
    checkExistOnStructures,
    isLoading,
  };
}
